import {Flex, Box, Text, Tooltip} from '@mantine/core';
import classNames from 'classnames';
import React, {useCallback, useEffect, useRef} from 'react';
import isEqual from 'react-fast-compare';

import DocumentEditorContext from 'notebook/documents/DocumentEditorContext';
import {TabEvent} from 'notebook/mediocre/EditorTypes';
// @ts-expect-error
import * as selutil from 'notebook/mediocre/selection/text/SelUtil';
import {LastSyncedUpstreamValue} from 'notebook/structured-tables/StructuredTableConstants';
import StructuredTableContext from 'notebook/structured-tables/util/StructuredTableContext';
import {TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import {useTimedCellEnterHandler} from 'notebook/tables/core-ui/cell/useTimedCellEnterHandler';
import FormulaEditable from 'notebook/tables/core-ui/FormulaEditable';
import {TableEditableClass} from 'notebook/tables/core-ui/TableEditable';
import {TriangleState} from 'notebook/tables/errors/TableErrorConstants';
import {EvaluatedCellError} from 'notebook/tables/excess/EvaluationConstants';
import {colNumberToLetters} from 'notebook/tables/excess/Util';
import {setCellEditorPosition} from 'notebook/tables/spreadsheets/SpreadsheetActions';
import SpreadsheetStore from 'notebook/tables/spreadsheets/SpreadsheetStore';
import {useCellModel} from 'notebook/tables/store/hooks/useCellModel';
import getTestProps from 'test/lib/cypress/getTestProps';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as keyboard from 'util/Keyboard';
import {ellipsize, EllipsisDirection} from 'util/NameUtil';
import {ElementKeyboardEvent, Optional} from 'util/Types';

import ErrorTrianglePopover from '../errors/ErrorTrianglePopover';

type CellIndex = [number, number]; // [i, j]

export interface TableCellEditorProps {
  fileId: string;
  tableName: string;
  tableNamespace: string;
  getLastIndex: () => TableIndex;
  cid: string;
  i: number;
  j: number;

  // Only available if the cell is part of a StructuredTable and was previously submitted or refreshed
  lastSyncedUpstreamValue?: LastSyncedUpstreamValue;

  // "Stateful" props
  highlightClass?: string;

  // Actions
  storeState: TableEditableClass['saveMultiSelect'];
  commitCell: TableEditableClass['updateCell'];
  moveFromCell: TableEditableClass['moveFromCell'];

  // Event handlers
  cellEnterHandler: TableEditableClass['cellEnterHandler'];
  cellEscapeHandler: TableEditableClass['cellEscapeHandler'];
  // Set if cell should show the fill down square
  keyDownHook: TableEditableClass['hideHoverCell'];

  // Formulas
  disableFormulas?: boolean;

  // Cell error for displaying error indicators
  cellError?: Optional<EvaluatedCellError>;

  // The stepId being displayed if in cross-step
  stepIdBeingDisplayed?: string;

  // Floating mode
  floating?: {
    navigateToEditingCell?: () => void;
    stepName?: string;
  };
}

interface TableCellEditorLabelProps {
  tableName: string;
  stepName?: string;
  cellReference: string;
  editorWidth: number;
  onLabelClick?: () => void;
}

const LABEL_ELLIPSIS_THRESHOLD = 20;

function TableCellEditorLabel({
  tableName,
  stepName,
  cellReference,
  editorWidth,
  onLabelClick,
}: TableCellEditorLabelProps): JSX.Element {
  const isStepNameEllipsized = stepName != null && stepName.length > LABEL_ELLIPSIS_THRESHOLD;
  const isTableNameEllipsized = tableName.length > LABEL_ELLIPSIS_THRESHOLD;
  const isEllipsized = isStepNameEllipsized || isTableNameEllipsized;

  return (
    <Tooltip
      label={
        <Box style={{width: editorWidth}}>
          <Text size="sm" style={{wordWrap: 'break-word', whiteSpace: 'normal'}}>
            {stepName && stepName.length > 0 ? `${stepName} / ` : ''}
            {tableName && tableName.length > 0 ? `${tableName} / ` : ''}
            {cellReference}
          </Text>
        </Box>
      }
      disabled={!isEllipsized}
      position="top"
      withArrow={true}
      arrowPosition="center"
      arrowSize={10}
      closeDelay={300}
      events={{hover: true, focus: false, touch: true}}
      styles={{
        tooltip: {
          backgroundColor: 'white',
          color: 'black',
          border: '1px solid #e0e0e0',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          padding: '8px 12px',
          pointerEvents: 'all',
        },
      }}
    >
      <Text
        className="mediocre-tableEditable-tableCellEditorLabel"
        {...getTestProps({component: 'TableCellEditor', element: 'label'})}
        {...(onLabelClick && {onClick: onLabelClick})}
      >
        {stepName && stepName.length > 0 && (
          <>
            {ellipsize(stepName, LABEL_ELLIPSIS_THRESHOLD, EllipsisDirection.MIDDLE)}
            &nbsp;!&nbsp;
          </>
        )}
        {tableName && tableName.length > 0 && (
          <>
            {ellipsize(tableName, LABEL_ELLIPSIS_THRESHOLD, EllipsisDirection.MIDDLE)}
            &nbsp;!&nbsp;
          </>
        )}
        {cellReference}
      </Text>
    </Tooltip>
  );
}

export function TableCellEditor({
  fileId,
  cid,
  i,
  j,
  lastSyncedUpstreamValue,
  highlightClass,
  tableNamespace,
  storeState,
  commitCell,
  getLastIndex,
  cellEnterHandler,
  cellEscapeHandler,
  keyDownHook,
  moveFromCell,
  tableName,
  disableFormulas,
  cellError,
  floating,
  stepIdBeingDisplayed,
}: TableCellEditorProps): JSX.Element {
  const {structuredTableKind} = StructuredTableContext.useContext();
  const {documentName} = DocumentEditorContext.useContext();
  const {cell, evaluatedCell, columnConstraint = null, rowData = null} = useCellModel(j);
  const cellHeader = useRef<HTMLDivElement>(null);

  const timedCellEnterHandler = useTimedCellEnterHandler({
    enabled: window.hasOwnProperty('Cypress'),
    text: cell.text,
    cellEnterHandler,
  });

  const index: CellIndex = [i, j];

  // Move right-down/left-up through rows.
  const handleFormulaTabKeyDown = (formula: string, {shiftKey}: TabEvent): void => {
    // Save selection to history before making change.
    storeState();

    // Update the current cell before leaving it
    commitCell(index, formula);
    moveFromCell({index, text: formula, dir: shiftKey ? 'left' : 'right', wrap: true});
  };

  const handleCellTabKeyDown = ({shiftKey}: ElementKeyboardEvent): void => {
    // Save selection to history before making change.
    storeState();
    moveFromCell({index, dir: shiftKey ? 'left' : 'right', wrap: true});
  };

  const handleFormulaArrowKeyDown = (formula: string, e: ElementKeyboardEvent): void => {
    // Must be collapsed, with no shift key.
    const selection = selutil.getSelectionIndices();
    if (e.shiftKey || !selection || selection.start !== selection.end) {
      return;
    }
    const cursor = selection.start;

    // Must not be a formula
    const formulaLength = formula.length;
    if (formulaLength && formula[0] === '=') {
      return;
    }

    // Determine new selection
    const dir = (() => {
      if (keyboard.isLeftKey(e) && cursor === 0) {
        return 'left';
      } else if (keyboard.isRightKey(e) && cursor === formulaLength) {
        return 'right';
      } else if (keyboard.isUpKey(e)) {
        return 'up';
      } else if (keyboard.isDownKey(e)) {
        return 'down';
      }
      return null;
    })();
    if (dir == null) {
      return;
    }

    // Update the cell
    commitCell(index, formula);
    moveFromCell({index, dir, text: formula, wrap: false});
  };

  const cellEditorRef = useRef<HTMLDivElement>(null);
  const dragHandlersRef = useRef<{
    handleMouseMove: (e: MouseEvent) => void;
    handleMouseUp: (e: MouseEvent) => void;
  } | null>(null);

  const {position, selection} = useStoreSelector(
    SpreadsheetStore,
    (store) => {
      const storeState = store.getState(tableNamespace, tableName, fileId);
      return {
        position: storeState.cellEditorPosition ?? null,
        selection: storeState.selection ?? null,
      };
    },
    isEqual
  );

  const focusTextInput = useCallback((): void => {
    const container = cellEditorRef.current as HTMLElement | null;

    const editableElement = container?.querySelector('input, [contenteditable="true"]') as HTMLElement | null;
    if (editableElement) {
      // Use the stored selection if available, otherwise default to end
      if (selection && selection.cid === cid) {
        selutil.restoreSelection(editableElement, {start: selection.start, end: selection.end});
      } else {
        const textLength = editableElement.textContent?.length ?? 0;
        selutil.restoreSelection(editableElement, {start: textLength, end: textLength});
      }
    }
  }, [selection, cid]);

  const focusTextInputRef = useRef(focusTextInput);
  focusTextInputRef.current = focusTextInput;

  // Cleanup the drag handlers if the component unmounts before the drag is complete
  useEffect(() => {
    if (dragHandlersRef.current) {
      document.removeEventListener('mousemove', dragHandlersRef.current.handleMouseMove);
      document.removeEventListener('mouseup', dragHandlersRef.current.handleMouseUp);
      dragHandlersRef.current = null;
    }
  }, []);

  const handleDragHandleMouseDown = (e: React.MouseEvent): boolean => {
    if (!floating || !position) {
      return false;
    }

    const cellEditorElement = cellEditorRef.current;
    if (!cellEditorElement) {
      return false;
    }

    // Prevent focus loss
    e.preventDefault();

    const handleMouseMove = (e: MouseEvent): void => {
      // Offset the difference between the drag handle position and the cell editor position
      const offsetX = e.clientX - position.left - 12;
      const offsetY = e.clientY - position.top + 10;
      cellEditorElement.style.transform = `translate(${offsetX}px, ${offsetY}px)`;

      const header = cellHeader.current;
      if (header) {
        header.classList.add('is-dragging');
      }
    };

    const handleMouseUp = (e: MouseEvent): void => {
      // Offset the difference between the drag handle position and the cell editor position
      setCellEditorPosition({
        ...position,
        left: e.clientX - 12,
        top: e.clientY + 10,
      });

      const header = cellHeader.current;
      if (header) {
        header.classList.remove('is-dragging');
      }
      cellEditorElement.style.transform = '';

      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      dragHandlersRef.current = null;

      // After completing a drag, refocus the editor
      setTimeout(focusTextInput, 0);
    };

    dragHandlersRef.current = {handleMouseMove, handleMouseUp};
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return true;
  };

  // Focus the text input when the component mounts or when displayed step switches
  useEffect(() => {
    focusTextInputRef.current();
  }, [stepIdBeingDisplayed]);

  const cellEditorContent = (
    <div
      className={classNames('mediocre-tableEditable-tableCellEditorCell', highlightClass)}
      style={{width: position?.width}}
      role="cell"
      aria-current="true"
      data-cid={cid}
      data-namespace={tableNamespace}
      {...getTestProps({component: 'TableCellEditor', element: 'root'})}
    >
      <FormulaEditable
        cell={cell}
        evaluatedCell={evaluatedCell}
        fileId={fileId}
        tableName={tableName}
        tableNamespace={tableNamespace}
        documentName={documentName ?? null}
        getLastIndex={getLastIndex}
        formulaArrowKeyDown={handleFormulaArrowKeyDown}
        formulaTabKeyDown={handleFormulaTabKeyDown}
        cellTabKeyDown={handleCellTabKeyDown}
        onFormulaFinalize={timedCellEnterHandler}
        onFormulaFocus={() => {}}
        keyDownHook={keyDownHook}
        escapeHandler={cellEscapeHandler}
        index={index}
        cid={cid}
        columnDefinition={columnConstraint && columnConstraint.columnDefinition}
        isFormulaBox={false}
        structuredTableKind={structuredTableKind}
        lastSyncedUpstreamValue={lastSyncedUpstreamValue}
        rowMetadata={rowData?.rowMetadata ?? null}
        disableFormulas={disableFormulas}
      />
    </div>
  );

  if (!position) {
    // Render off-screen but in DOM to captures input while scrolling to position.
    // Using fixed position off-screen so focusing it doesn't scroll the page.
    return (
      <div
        style={{position: 'fixed', top: -9999, left: -9999, opacity: 0}}
        {...getTestProps({component: 'TableCellEditor', element: 'hidden'})}
      >
        {cellEditorContent}
      </div>
    );
  }

  if (!floating) {
    return (
      <Box
        ref={cellEditorRef}
        className="mediocre-tableEditable-tableCellEditor--floating js-mediocre-active-edit"
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
        style={{
          top: position.top,
          left: position.left,
          minHeight: position.minHeight,
        }}
      >
        <div>{cellEditorContent}</div>
      </Box>
    );
  }

  return (
    <Box
      ref={cellEditorRef}
      className="mediocre-tableEditable-tableCellEditor--floating js-mediocre-active-edit"
      onMouseDown={(e) => {
        e.stopPropagation();
      }}
      style={{
        top: position.top,
        left: position.left,
        minHeight: position.minHeight,
      }}
      {...getTestProps({component: 'TableCellEditor', element: 'floating'})}
    >
      <div
        className="mediocre-tableEditable-tableCellEditorHeader"
        ref={cellHeader}
        {...getTestProps({component: 'TableCellEditor', element: 'header'})}
      >
        <div
          className="axisCell-dragHandle-container axisCell-dragHandle-container--row"
          onMouseDown={handleDragHandleMouseDown}
          style={{cursor: 'grab'}}
          {...getTestProps({component: 'TableCellEditor', element: 'dragHandle'})}
        >
          <div className="axisCell-dragHandle" />
        </div>
        <Flex direction="row" align="center" w="100%" justify="space-between">
          <TableCellEditorLabel
            tableName={tableName}
            stepName={floating.stepName}
            cellReference={`${colNumberToLetters(j)}${i + 1}`}
            editorWidth={position.width}
            {...(floating.navigateToEditingCell && {
              onLabelClick: () => {
                floating.navigateToEditingCell?.();
                focusTextInput();
              },
            })}
          />
          {cellError && (
            <ErrorTrianglePopover
              key="error-popover"
              errorState={TriangleState.ILLEGAL}
              error={cellError}
              placement={FloatingPlacement.BOTTOM}
            >
              <span className="u-valign-middle u-tiny-spacing-left text-danger">
                <BenchlingIcon icon="warning" />
              </span>
            </ErrorTrianglePopover>
          )}
        </Flex>
      </div>
      <div>{cellEditorContent}</div>
    </Box>
  );
}
